// src/pages/AddItemFinalDeepInnerSubCategory.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import InventoryNav from "../components/inventoryNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { Link, useParams } from "react-router-dom";
import EditItemCategoryModal from "../components/addItemEditModal";
import { hasPermission } from "../utils/permissionHelper";
import BulkFinalItemUpload from "../components/bulkFinalItemUpload";

const API = process.env.REACT_APP_API_BASE || "https://rfs2.infraconsoft.net.in/api";
const PAGE_SIZE = 10;

export default function AddItemFinalDeepInnerSubCategory() {
  useAuthRedirect();

  // route params:
  const { catCode, itemName, subCode, subItem, innerCode, innerItem, deepCode, deepItem } = useParams();

  const branch = localStorage.getItem("branch");
  const userId = localStorage.getItem("username");

  const canView  = hasPermission("Item Code Generation", "view");
  const canEntry = hasPermission("Item Code Generation", "entry");
  const canEdit  = hasPermission("Item Code Generation", "edit");
  const canDel   = hasPermission("Item Code Generation", "del");

  const [formData, setFormData] = useState({ catName: "", subCatCode: "", partNo: "" });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ id: "", catName: "" });

  useEffect(() => {
    if (!deepCode || !catCode) return;
    const run = async () => {
      await fetchNextCode();
      await fetchPage(1);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepCode, catCode]);

  const fetchNextCode = async () => {
    try {
      Swal.fire({ title: "Loading Item Code", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const { data } = await axios.get(`${API}/itemFinalDeepInnerSubCat_no`, { params: { mainCode: catCode } });
      const code = data?.newCode || data?.newSubCatCode;
      if (!code) throw new Error(data?.error || "No code returned.");
      setFormData((p) => ({ ...p, subCatCode: code }));
      Swal.close();
    } catch (e) {
      Swal.close();
      Swal.fire({ icon: "error", title: "Error", text: e.response?.data?.error || e.message });
    }
  };

  const fetchPage = async (p, showLoader = true) => {
    if (!deepCode) return;
    try {
      if (showLoader) setLoading(true);
      const { data } = await axios.get(`${API}/itemFinalDeepInnerSubCat_list`, {
        params: { catCode: deepCode, page: p, pageSize: PAGE_SIZE, q }
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (err) {
      console.error("Error fetching list:", err);
      Swal.fire({ icon: "error", title: "Error", text: "Failed to load items." });
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // debounce search
  useEffect(() => {
    const id = setTimeout(() => {
      fetchPage(1);
    }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.catName.trim()) {
      Swal.fire({ icon: "warning", title: "Empty Field", text: "Please enter a valid Item name!" });
      return;
    }

    const payload = {
      branch,
      user: userId,
      mainCode: deepCode,               // linkcode
      subCode: formData.subCatCode,     // FIC...
      name: formData.catName.trim(),
      partNo: formData.partNo?.trim() || null,
    };

    try {
      Swal.fire({ title: "Submitting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const { data } = await axios.post(`${API}/itemFinalDeepInnerSubCat_insert`, payload);
      if (data.success) {
        Swal.fire({ icon: "success", title: "Success", text: "Item added!" });
        await fetchPage(1);
        await fetchNextCode();
        setFormData((p) => ({ ...p, catName: "", partNo: "" }));
      } else {
        Swal.fire({ icon: "error", title: "Error", text: data.message || "Submission failed." });
      }
    } catch (e) {
      Swal.fire({ icon: "error", title: "Error", text: e.response?.data?.message || e.message });
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        Swal.fire({ title: "Submitting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        await axios.delete(`${API}/itemFinalDeepInnerSubCat_delete/${id}`);
        Swal.fire({ title: "Deleted!", text: "Your record has been deleted.", icon: "success" });
        await fetchPage(page);
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire({ title: "Error!", text: "Failed to delete item.", icon: "error" });
      }
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const goTo = (p) => {
    const clamped = Math.max(1, Math.min(p, totalPages));
    fetchPage(clamped);
  };

  const openEditModal = (item) => {
    setEditFormData({ id: item.id, catNo: item.code, catName: item.itemName });
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => setIsEditModalOpen(false);

  if (!canView) {
    return (
      <div id="admin-container">
        <InventoryNav />
        <div className="content-right" style={{ padding: 20 }}>No access</div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <InventoryNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Final Item</h2>
            <Link to={`/inventory/add_deep_Inner_Subcategory/${catCode}/${itemName}/${subCode}/${subItem}/${innerCode}/${innerItem}`}>
              <p className="back-button"><i className="fa-solid fa-backward"></i>Back</p>
            </Link>
          </div>
          <Time />
        </section>

        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          {canEntry && (
            <button className="submit-btn" type="button" onClick={() => setBulkOpen(true)}>
              CSV Upload
            </button>
          )}
        </div>

        <div className="form-container">
          {canEntry && (
            <form className="vehicle-form" onSubmit={handleSubmit}>
              {/* Category Info */}
              <div className="form-row">
                <div className="form-group">
                  <label>Category Code :</label>
                  <input type="text" value={catCode} readOnly style={{ background: '#f8c9bf', fontWeight: 'bold' }} />
                </div>
                <div className="form-group">
                  <label>Category Name :</label>
                  <input type="text" value={itemName} readOnly style={{ background: '#f8c9bf', fontWeight: 'bold' }} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Sub Category Code :</label>
                  <input type="text" value={subCode} readOnly style={{ background: '#f8c9bf', fontWeight: 'bold' }} />
                </div>
                <div className="form-group">
                  <label>Sub Category Name :</label>
                  <input type="text" value={subItem} readOnly style={{ background: '#f8c9bf', fontWeight: 'bold' }} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Inner Sub Category Code :</label>
                  <input type="text" value={innerCode} readOnly style={{ background: '#f8c9bf', fontWeight: 'bold' }} />
                </div>
                <div className="form-group">
                  <label>Inner Sub Category Name :</label>
                  <input type="text" value={innerItem} readOnly style={{ background: '#f8c9bf', fontWeight: 'bold' }} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Deep Inner Sub Category Code :</label>
                  <input type="text" value={deepCode} readOnly style={{ background: '#f8c9bf', fontWeight: 'bold' }} />
                </div>
                <div className="form-group">
                  <label>Deep Inner Sub Category Name :</label>
                  <input type="text" value={deepItem} readOnly style={{ background: '#f8c9bf', fontWeight: 'bold' }} />
                </div>
              </div>

              {/* New Item Fields */}
              <div className="form-row">
                <div className="form-group">
                  <label>Item Code :</label>
                  <input type="text" name="subCatCode" value={formData.subCatCode} readOnly />
                </div>
                <div className="form-group">
                  <label>Item Name :</label>
                  <input
                    type="text"
                    name="catName"
                    value={formData.catName}
                    onChange={handleChange}
                    placeholder="Enter Item Name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Part No (optional):</label>
                  <input
                    type="text"
                    name="partNo"
                    value={formData.partNo}
                    onChange={handleChange}
                    placeholder="Enter Part No (optional)"
                  />
                </div>
              </div>

              <div className="btn-cls">
                <button type="submit" className="submit-btn">Save</button>
              </div>
            </form>
          )}

          {/* Search */}
          <div className="search-filter">
            <p>Search: </p>
            <input
              type="text"
              placeholder="Search by code, name, or part no"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="search-input"
            />
            <span style={{ marginLeft: 10, opacity: 0.7 }}>Total: {total}</span>
          </div>

          {/* Table */}
          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Item</th>
                  {canEdit && <th>Edit</th>}
                  <th>Part No</th>
                  {canDel && <th>Delete</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="loading-text">Loading...</td></tr>
                ) : items.length > 0 ? (
                  items.map((item, idx) => (
                    <tr key={item.id ?? `${item.code}-${idx}`}>
                      <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                      <td>{item.code} - ({item.itemName})</td>
                      {canEdit && (
                        <td>
                          <button
                            style={{ background: "transparent", border: "none", color: "blue", cursor: "pointer" }}
                            title="Edit"
                            onClick={() => openEditModal(item)}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                        </td>
                      )}
                      <td>{item.partNo || "-"}</td>
                      {canDel && (
                        <td className="del">
                          {item.childCount > 0 ? "No Access" : (
                            <i className="fa-solid fa-trash-can" onClick={() => handleDelete(item.id)}></i>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6">No Data Available</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {/* Pagination */}
<div className="pagination">
  <button onClick={() => goTo(1)} disabled={page === 1}>
    First
  </button>
  <button onClick={() => goTo(page - 1)} disabled={page === 1}>
    Previous
  </button>

  {(() => {
    const windowSize = 2; // how many neighbors around current page
    let start = Math.max(2, page - windowSize);
    let end = Math.min(totalPages - 1, page + windowSize);

    const buttons = [];

    // Always show page 1
    buttons.push(
      <button key={1} onClick={() => goTo(1)} className={page === 1 ? "active" : ""}>
        1
      </button>
    );

    // Left dots
    if (start > 2) buttons.push(<span key="dots-left">…</span>);

    // Middle range
    for (let p = start; p <= end; p++) {
      buttons.push(
        <button key={p} onClick={() => goTo(p)} className={page === p ? "active" : ""}>
          {p}
        </button>
      );
    }

    // Right dots
    if (end < totalPages - 1) buttons.push(<span key="dots-right">…</span>);

    // Always show last page (if more than 1)
    if (totalPages > 1) {
      buttons.push(
        <button
          key={totalPages}
          onClick={() => goTo(totalPages)}
          className={page === totalPages ? "active" : ""}
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  })()}

  <button onClick={() => goTo(page + 1)} disabled={page === totalPages}>
    Next
  </button>
  <button onClick={() => goTo(totalPages)} disabled={page === totalPages}>
    Last
  </button>
</div>

        </div>
      </div>

      <EditItemCategoryModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        fetchData={() => fetchPage(page)}
      />

      {canEntry && (
        <BulkFinalItemUpload
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onCommitted={() => { fetchPage(1); fetchNextCode(); }}
          mainCode={catCode}
          deepCode={deepCode}
        />
      )}
    </div>
  );
}
